import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowRight, Building2, CreditCard, ShieldCheck, AlertTriangle, Lock, Tag } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Chandigarh","Jammu and Kashmir","Ladakh","Puducherry"];

function Input({ label, required, type = "text", value, onChange, placeholder, mono }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">
        {label}{required && <span className="text-primary ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={`w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all ${mono ? "font-mono tracking-wider" : ""}`}
      />
    </div>
  );
}

function Select({ label, required, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">
        {label}{required && <span className="text-primary ml-0.5">*</span>}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all appearance-none"
      >
        <option value="">Select state</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-3 border-b border-border">
        <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-[0.15em] text-primary font-medium">{title}</span>
      </div>
      {children}
    </div>
  );
}

export default function PartnerRegister() {
  const branding = useBranding();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Referral link attribution from URL
  const linkCode         = searchParams.get("link_code") || "";
  const refPartnerId     = searchParams.get("ref") || "";
  const urlPlanId        = searchParams.get("plan_id") || "";
  const urlCycle         = searchParams.get("cycle") || "";

  const [linkMeta, setLinkMeta] = useState(null);
  const [linkError, setLinkError] = useState(false);  // true if link fetch failed

  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    pan_number: "", aadhaar_or_business_id: "", gst_number: "", address: "", state: "",
    bank_account_number: "", bank_ifsc: "", bank_name: "", bank_account_holder: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // Fetch link metadata if link_code is in URL
  useEffect(() => {
    if (!linkCode) return;
    axios.get(`${API}/api/join/${linkCode.toUpperCase()}`)
      .then(r => setLinkMeta(r.data))
      .catch(() => setLinkError(true));  // hide banner on invalid/expired link
  }, [linkCode]);

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        ...form,
        // Referral attribution — silently passed, never editable by user
        ...(linkCode     && { referral_link_code:     linkCode.toUpperCase() }),
        ...(refPartnerId && { referred_by_partner_id: refPartnerId }),
        ...(urlPlanId    && { plan_id:                urlPlanId }),
        ...(urlCycle     && { billing_cycle:           urlCycle }),
      };
      await axios.post(`${API}/api/sm/partners/register`, payload);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Block firm employees and active partners from registering again
  const isTenantUser = user && user.tenant_id;
  const isPartner    = user && user.role === "partner";

  if (isTenantUser || isPartner) return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 partner-portal">
      <div className="max-w-md w-full">
        <div className="bg-card border border-amber-500/30 rounded-2xl p-8 text-center shadow-lg">
          <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-7 h-7 text-amber-500" strokeWidth={1.5} />
          </div>
          {isPartner ? (
            <>
              <h2 className="font-semibold text-foreground text-lg mb-2">Already a Partner</h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                You already have an active partner account. Please sign in to the Partner Portal to access your earnings and referrals.
              </p>
              <Link to="/partner/login"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:opacity-90 transition-colors">
                Go to Partner Portal <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <>
              <h2 className="font-semibold text-foreground text-lg mb-2">Firm Account Detected</h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                You are currently signed in as a <span className="font-medium text-foreground">{user.role?.replace(/_/g, " ")}</span> on the <span className="font-medium text-foreground">{branding.app_name || "Law OS"}</span> firm workspace.
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                Partner registration requires an independent, personal email address — not one associated with a firm workspace. Please sign out and use a different email to apply.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/app"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-border text-sm text-muted-foreground rounded-xl hover:bg-muted transition-colors">
                  Back to Workspace
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 partner-portal">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-primary" strokeWidth={1.5} />
        </div>
        <h2 className="font-serif text-2xl text-foreground mb-3">Application Submitted</h2>
        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
          Your KYC application has been received. Our team will review your details and email your login credentials once approved.
        </p>
        <Link to="/partner/login"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:opacity-90 transition-colors">
          Go to Login <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background py-12 px-4 partner-portal">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2.5 mb-6">
            {branding.logo_src ? (
              <img src={branding.logo_src} alt={branding.app_name}
                style={{ height: 32, width: "auto", maxWidth: 100 }}
                className="object-contain"
                onError={e => { e.target.style.display = "none"; }} />
            ) : (
              <div className="w-8 h-8 border-2 border-primary rounded-md flex items-center justify-center shrink-0">
                <span className="font-serif text-primary font-bold">{(branding.app_name || "P")[0]}</span>
              </div>
            )}
            <div className="text-left">
              <div className="font-serif text-foreground text-sm leading-none font-semibold">{branding.app_name || "InnoAim"}</div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-primary">Partner Portal</div>
            </div>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-foreground font-light mb-2">Become a Partner</h1>
          <p className="text-muted-foreground text-sm">Register to earn commissions by referring law firms to Law OS</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="partner-register-form">
        {/* Referral link attribution banner — only shown when link is valid */}
        {(linkMeta || (linkCode && !linkError)) && (
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 flex items-start gap-3" data-testid="referral-attribution-banner">
            <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 mt-0.5">
              <Lock className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.16em] text-primary font-semibold mb-0.5">Plan Pre-selected</p>
              <div className="flex flex-wrap items-center gap-2">
                {linkMeta ? (
                  <>
                    <span className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
                      <Tag className="w-3.5 h-3.5 text-primary" />
                      {linkMeta.plan_name}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-semibold">
                      {linkMeta.billing_cycle === "yearly" ? "Annual" : "Monthly"}
                    </span>
                    {linkMeta.price_inr > 0 && (
                      <span className="text-[12px] text-muted-foreground">
                        ₹{Number(linkMeta.price_inr).toLocaleString("en-IN")}/{linkMeta.billing_cycle === "yearly" ? "yr" : "mo"}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-[13px] text-muted-foreground">Loading plan details…</span>
                )}
              </div>
              {linkMeta?.partner_name && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Referred by <strong className="text-foreground">{linkMeta.partner_name}</strong>
                </p>
              )}
            </div>
            <span className="shrink-0 text-[9px] uppercase tracking-[0.2em] text-primary/60 bg-primary/5 border border-primary/10 px-2 py-1 rounded-full font-medium mt-0.5">
              Locked
            </span>
          </div>
        )}

        {/* Personal Info */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
            <Section icon={Building2} title="Personal & Business Information">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Full Name" required value={form.name} onChange={set("name")} placeholder="Rajesh Kumar" />
                <Input label="Email Address" required type="email" value={form.email} onChange={set("email")} placeholder="rajesh@example.com" />
                <Input label="Phone Number" required value={form.phone} onChange={set("phone")} placeholder="+91 9876543210" />
                <Input label="Company / Firm Name" value={form.company} onChange={set("company")} placeholder="Optional" />
              </div>
            </Section>
          </div>

          {/* KYC */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
            <Section icon={ShieldCheck} title="KYC Verification">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="PAN Number" required value={form.pan_number} onChange={set("pan_number")} placeholder="ABCDE1234F" mono />
                <Input label="Aadhaar / Business Reg. No." value={form.aadhaar_or_business_id} onChange={set("aadhaar_or_business_id")} placeholder="Optional" mono />
                <Input label="GST Number" value={form.gst_number} onChange={set("gst_number")} placeholder="Optional" mono />
                <Select label="State" required value={form.state} onChange={set("state")} options={STATES} />
                <div className="sm:col-span-2">
                  <Input label="Address" value={form.address} onChange={set("address")} placeholder="Street, City, Pincode" />
                </div>
              </div>
            </Section>
          </div>

          {/* Bank Details */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
            <Section icon={CreditCard} title="Bank Details for Commission Payouts">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Account Number" required value={form.bank_account_number} onChange={set("bank_account_number")} placeholder="Account number" mono />
                <Input label="IFSC Code" required value={form.bank_ifsc} onChange={set("bank_ifsc")} placeholder="SBIN0001234" mono />
                <Input label="Bank Name" required value={form.bank_name} onChange={set("bank_name")} placeholder="State Bank of India" />
                <Input label="Account Holder Name" value={form.bank_account_holder} onChange={set("bank_account_holder")} placeholder="As per bank records" />
              </div>
            </Section>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-xl px-4 py-3" data-testid="register-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} data-testid="partner-register-submit"
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-colors text-sm">
            {loading ? "Submitting..." : <>Submit Application <ArrowRight className="w-4 h-4" /></>}
          </button>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/partner/login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
